// ============================================
// HIGH-DYNAMIC-RANGE GRAPH SCALING
// ============================================
//
// High-degree polynomials span many orders of magnitude in y (a degree-10 curve
// can reach ±10^5 within a small x-window). On a fixed linear grid such curves
// collapse into near-vertical spikes and the pedagogically important features —
// where the curve crosses vs. bounces at each root, and its end behavior — become
// invisible, so the "pick the matching graph" variations are indistinguishable.
//
// When (and only when) a curve overflows the grid window by a large factor in its
// interesting region, y is remapped with a sign-preserving log compression
// y' = sign(y)·log10(1+|y|)·S whose scale S depends ONLY on the grid bounds and a
// fixed magnitude cap — never on the curve's own extrema. That makes it:
//  - shareable across a question's four variations — curves with the same true
//    y-intercept still draw at the same height (per-curve fit-to-max scaling did not);
//  - smooth over the whole range (a hard soft-clip saturated extreme curves into
//    flat-topped plateaus that read as rendering artifacts);
//  - safe for asymmetric windows (each sign compresses toward its own edge).

// Compress only when a curve's true magnitude (within its focus range) exceeds the
// window edge by more than this factor. Below it the curve simply clips, as always.
export const COMPRESSION_OVERFLOW_RATIO = 3;

// The compressed curve fills at most this fraction of the window edge.
const FILL_MARGIN = 0.92;

// Magnitude that maps to the full FILL_MARGIN·edge height; larger values saturate.
const MAGNITUDE_CAP = 1e6;

/**
 * Build a y-axis compression shared by a group of curves (a question's variations).
 *
 * @param {Array<Array<{x:number, y:number}>>} pointArrays - One sample array per curve.
 * @param {{yMin:number, yMax:number}} gridBounds
 * @param {Array<{xMin:number, xMax:number}|undefined>} [focusRanges] - Per-curve
 *   x-range used for the overflow decision (typically the span around the curve's
 *   roots). Falls back to the whole curve when absent.
 * @returns {{apply: (points: Array) => Array, compressed: boolean}} `apply` remaps a
 *   point array (identity — the same reference — when no compression is needed).
 */
export function createSharedYCompression(pointArrays, gridBounds, focusRanges) {
  const yMin = gridBounds?.yMin ?? -10;
  const yMax = gridBounds?.yMax ?? 10;
  const posEdge = Math.max(yMax, 1e-9);
  const negEdge = Math.max(-yMin, 1e-9);
  const gate = Math.max(posEdge, negEdge) * COMPRESSION_OVERFLOW_RATIO;

  let compressed = false;
  (pointArrays || []).forEach((points, i) => {
    if (compressed || !Array.isArray(points)) return;
    const focus = focusRanges?.[i];
    for (const p of points) {
      if (!Number.isFinite(p.y)) continue;
      if (focus && (p.x < focus.xMin || p.x > focus.xMax)) continue;
      if (Math.abs(p.y) > gate) {
        compressed = true;
        break;
      }
    }
  });

  if (!compressed) {
    return { apply: (points) => points, compressed: false };
  }

  // Fixed scale: log10(1 + CAP) maps to FILL_MARGIN of the window edge. Content-
  // independent, so every curve in the group shares the exact same mapping.
  const capLog = Math.log10(1 + MAGNITUDE_CAP);
  const mapY = (y) => {
    if (!Number.isFinite(y)) return y;
    const edge = y >= 0 ? posEdge : negEdge;
    const a = Math.min(Math.abs(y), MAGNITUDE_CAP);
    return Math.sign(y) * Math.log10(1 + a) * ((FILL_MARGIN * edge) / capLog);
  };

  return {
    apply: (points) => points.map((p) => (Number.isFinite(p.y) ? { ...p, y: mapY(p.y) } : p)),
    compressed: true,
  };
}
